import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs } from '@/components/ui';
import { paths } from '@/routes/paths';
import AppearanceSection from '@/features/settings/AppearanceSection';
import ViewAsSection from '@/features/settings/ViewAsSection';
import ProfileTab from '@/features/settings/ProfileTab';
import ApiKeysTab from '@/features/settings/ApiKeysTab';
import '@/styles/pages/settings.css';

type SettingsTab = 'api-keys' | 'profile' | 'appearance';

const SETTINGS_TABS: { id: SettingsTab; label: string; path: string }[] = [
  { id: 'api-keys', label: 'API Keys', path: paths.settings.apiKeys },
  { id: 'profile', label: 'My Profile', path: paths.settings.profile },
  { id: 'appearance', label: 'Appearance', path: paths.settings.appearance },
];

const TAB_COPY: Record<SettingsTab, { title: string; subtitle: string }> = {
  'api-keys': {
    title: 'API Keys',
    subtitle:
      'Bring your own API keys for each provider. Your keys take precedence over org-level defaults and are used exclusively for your own runs.',
  },
  profile: {
    title: 'My Profile',
    subtitle: 'Manage your personal information, preferences, and account settings.',
  },
  appearance: {
    title: 'Appearance',
    subtitle: 'Choose how you want the app to look.',
  },
};

function isSettingsTab(tab: string | undefined): tab is SettingsTab {
  return tab === 'api-keys' || tab === 'profile' || tab === 'appearance';
}

export default function SettingsPage() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSettingsTab(tab)) {
      navigate(paths.settings.apiKeys, { replace: true });
    }
  }, [tab, navigate]);

  const active: SettingsTab = isSettingsTab(tab) ? tab : 'api-keys';
  const copy = TAB_COPY[active];

  return (
    <div className="settings-page">
      <header className="settings-header">
        <h1 className="settings-title">{copy.title}</h1>
        <p className="settings-subtitle">{copy.subtitle}</p>
      </header>

      <Tabs
        ariaLabel="Settings sections"
        className="quralyst-tabs--flush"
        tabs={SETTINGS_TABS.map(({ id, label }) => ({ id, label }))}
        active={active}
        onChange={(id) => {
          const next = SETTINGS_TABS.find((t) => t.id === id);
          if (next) navigate(next.path);
        }}
      />

      <div className="settings-content">
        {active === 'api-keys' && <ApiKeysTab />}
        {active === 'profile' && <ProfileTab />}
        {active === 'appearance' && <AppearanceSection />}
        {/* Platform-admin tier preview (admin/role-based-access §5). Renders nothing for
            anyone else, so this tab is unchanged for every other user. */}
        {active === 'appearance' && <ViewAsSection />}
      </div>
    </div>
  );
}
